// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

import type { SolidSlab, SolidSpace } from '../../api/types';
import { chooseLabelSpot, fitLabel, labelAnchor, ringsPath, ringsXyz, spaceLabelText, splitRings, textWidth } from '../../bygning/spaces';
import { slabPlanStyle } from '../../bygning/planSlabs';
import type { ViewportHandle } from '../../viewport/Viewport';
import styles from './PlanLayer.module.css';

/** The label size the fit is measured at: `--font-size-xs` (0.6875rem). */
const LABEL_FONT_PX = 11;
/** The marker sprite's radius on screen, roughly (`MARKER_SCALE` of a 900 px view). */
const MARKER_R = 9;
/** Where a heading arrow ends, from the marker centre (px). */
const ARROW_LEN = 30;
/** The label pill's padding, both sides together (px): `--space-2` across, `--space-1` down. */
const PILL_PAD_X = 16;
const PILL_PAD_Y = 8;
/** A label line's height at `--font-size-xs` × `--line-height-tight` (px). */
const LABEL_LINE_PX = 13;

/** One panorama marker as the plan layer draws its chrome. */
export interface PlanMarker {
  id: number;
  /** World position. */
  x: number;
  y: number;
  z: number;
  /** Heading, degrees about +z (0 = +x); null = unknown (no arrow). */
  yaw: number | null;
  /** Draw the heading arrow. */
  arrow: boolean;
  selected: boolean;
  /** A new panorama not yet uploaded: dashed. */
  pending?: boolean;
}

export type PlanDragKind = 'move' | 'turn';

export interface PlanLayerProps {
  viewport: RefObject<ViewportHandle | null>;
  /** Floor slabs and roof pieces (`planSlabs`): faint fills under the spaces, at their top face. */
  slabs: readonly SolidSlab[];
  /** The plan's cut height (world z), or null: a slab over it (and a roof) is drawn dashed, as above the cut. */
  cutZ?: number | null;
  /** Filled and named. */
  spaces: readonly SolidSpace[];
  /** Outline only. */
  ghostSpaces: readonly SolidSpace[];
  selectedSpace: string | null;
  /** Rediger › Geometri's multi-selection: every space in it is drawn selected (with `selectedSpace`). */
  selectedSpaces?: readonly string[];
  markers: readonly PlanMarker[];
  /** Every marker drawn on the plan (world xyz): the space names step aside for them. */
  avoid: readonly (readonly [number, number, number])[];
  /** The marker whose handles are live (Flyt), if any. */
  editId: number | null;
  /** A handle drag: the marker centre (move) or the pointer (turn) in world x/y. */
  onDrag?: (kind: PlanDragKind, id: number, world: [number, number], shift: boolean) => void;
  onDragEnd?: (kind: PlanDragKind, id: number) => void;
  onDragCancel?: () => void;
  /**
   * Bump to drop a drag in progress (Esc): the layer lets go of the pointer
   * and ignores the rest of that gesture; the host has already put the
   * marker back.
   */
  cancelToken: number;
  /** Bump to reproject (content landed, the scene's origin moved). */
  revision: number;
  /**
   * Rediger › Geometri's CAD plan: spaces unfilled (outline only when
   * selected), their names as quiet text — room colours belong to the room
   * tool, not to every edit.
   */
  cad?: boolean;
  /**
   * Room colours (S6): guid → a `--type-N` swatch, filled at `--room-alpha`.
   * Only the Rum and Zone tools pass them (design.md: room colours belong
   * to the room tool, not to every edit).
   */
  fills?: ReadonlyMap<string, string>;
}

/**
 * The locked plan's drawn layer, over the canvas (frontend-ayw.3/.8): the
 * focused storey's floor slabs and roof as faint fills underneath
 * (ruxd-jmt), its spaces as filled polygons (holes open) with name and
 * area, the other storeys' as outlines, and the panorama markers' chrome —
 * a ring on the selected one, heading arrows, and under Flyt the drag and
 * turn handles. Reprojected on every camera change (one rAF at most); only
 * the handles take the pointer, so panning and the markers underneath work
 * as before.
 */
export function PlanLayer({
  viewport,
  slabs,
  cutZ = null,
  spaces,
  ghostSpaces,
  selectedSpace,
  selectedSpaces,
  markers,
  avoid,
  editId,
  onDrag,
  onDragEnd,
  onDragCancel,
  cancelToken,
  revision,
  cad = false,
  fills,
}: PlanLayerProps) {
  const [tick, setTick] = useState(0);
  const hostRef = useRef<HTMLDivElement>(null);

  // Follow the camera and the host's size, at most once a frame.
  useEffect(() => {
    let frame: number | null = null;
    const bump = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        setTick((t) => t + 1);
      });
    };
    const off = viewport.current?.onCameraChange(bump) ?? (() => {});
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(bump);
    if (hostRef.current) observer?.observe(hostRef.current);
    bump();
    return () => {
      off();
      observer?.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [viewport, revision]);

  // World geometry per space, once: the rings at floor height and the label anchor.
  const geometry = useMemo(
    () =>
      [...spaces, ...ghostSpaces].map((s) => ({
        space: s,
        ghost: !spaces.includes(s),
        rings: ringsXyz(s.polygon, s.z_floor),
        anchor: labelAnchor(s.polygon),
        text: spaceLabelText(s),
      })),
    [spaces, ghostSpaces],
  );

  // World geometry per slab, once: the rings at its top face (a floor slab's top is the storey's floor).
  const slabGeometry = useMemo(() => slabs.map((s) => ({ slab: s, rings: ringsXyz(s.polygon, s.z_top) })), [slabs]);

  const handle = viewport.current;
  const drawn = useMemo(() => {
    if (!handle) return { slabPolys: [], polys: [], marks: [] };
    const project = (xyz: Float64Array) => handle.projectWorld(xyz, { clip: false });
    const avoidXy = project(new Float64Array(avoid.flatMap((p) => [p[0], p[1], p[2]])));
    const marks = markers.map((m) => {
      const yaw = m.yaw ?? 0;
      const rad = (yaw * Math.PI) / 180;
      const p = project(new Float64Array([m.x, m.y, m.z, m.x + Math.cos(rad), m.y + Math.sin(rad), m.z]));
      const len = Math.hypot(p[2] - p[0], p[3] - p[1]);
      const dir = len > 1e-6 ? [(p[2] - p[0]) / len, (p[3] - p[1]) / len] : [1, 0];
      return { m, x: p[0], y: p[1], dir };
    });
    const avoidPts: [number, number][] = [];
    for (let i = 0; i + 1 < avoidXy.length; i += 2) {
      if (Number.isFinite(avoidXy[i]) && Number.isFinite(avoidXy[i + 1])) avoidPts.push([avoidXy[i], avoidXy[i + 1]]);
    }
    // The heading arrows (their middle and tip, where the turn handle sits) are in the way too.
    for (const { m, x, y, dir } of marks) {
      if (!m.arrow || m.yaw === null || !Number.isFinite(x) || !Number.isFinite(y)) continue;
      avoidPts.push([x + dir[0] * ARROW_LEN * 0.6, y + dir[1] * ARROW_LEN * 0.6], [x + dir[0] * (ARROW_LEN + 10), y + dir[1] * (ARROW_LEN + 10)]);
    }
    const polys = geometry.map((g) => {
      const xy = project(g.rings.xyz);
      const rings = splitRings(xy, g.rings.counts);
      const d = ringsPath(rings);
      let label: null | { x: number; y: number; fit: ReturnType<typeof fitLabel> } = null;
      if (d && !g.ghost) {
        const a = project(
          new Float64Array([g.anchor.x, g.anchor.y, g.space.z_floor, g.anchor.x + 1, g.anchor.y, g.space.z_floor]),
        );
        const ppm = Math.hypot(a[2] - a[0], a[3] - a[1]);
        // Width: the outer ring's screen span; height: twice the distance to the nearest wall.
        let minX = Infinity;
        let maxX = -Infinity;
        for (let i = 0; i < rings[0].length; i += 2) {
          minX = Math.min(minX, rings[0][i]);
          maxX = Math.max(maxX, rings[0][i]);
        }
        const availW = Math.min(maxX - minX, 2 * g.anchor.radius * ppm * 2.2) - PILL_PAD_X - 8;
        const availH = 2 * g.anchor.radius * ppm - PILL_PAD_Y - 4;
        if (Number.isFinite(a[0]) && Number.isFinite(ppm)) {
          const fit = fitLabel(g.text, LABEL_FONT_PX, availW, availH);
          const widest = fit === 'one' ? g.text.line : fit === 'two' ? [g.text.name, g.text.area ?? ''].sort((p, q) => q.length - p.length)[0] : g.text.name;
          const size = {
            w: textWidth(widest, LABEL_FONT_PX) + PILL_PAD_X,
            h: (fit === 'two' ? 2 : 1) * LABEL_LINE_PX + PILL_PAD_Y,
          };
          // A panorama marker where the name would go (one usually stands mid-room) keeps its dot:
          // the name takes the first spot that stays in the room and off every marker, else the anchor.
          const spot = chooseLabelSpot([a[0], a[1]], size, { outer: rings[0], holes: rings.slice(1) }, avoidPts, MARKER_R);
          label = { x: spot?.x ?? a[0], y: spot?.y ?? a[1], fit };
        }
      }
      return { g, d, label };
    });
    const slabPolys = slabGeometry.map((g) => ({ g, d: ringsPath(splitRings(project(g.rings.xyz), g.rings.counts)) }));
    return { slabPolys, polys, marks };
    // `tick` is the camera; the handle's projection reads it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle, slabGeometry, geometry, markers, avoid, tick]);

  // --- handle drags ---------------------------------------------------------

  const drag = useRef<{
    kind: PlanDragKind;
    id: number;
    offset: [number, number];
    z: number;
    pointer: number;
    target: Element;
  } | null>(null);

  /** Let go of a drag: the pointer capture, and no text selection any more. */
  const release = () => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    try {
      if (d.target.hasPointerCapture(d.pointer)) d.target.releasePointerCapture(d.pointer);
    } catch {
      /* the element is gone */
    }
    document.body.classList.remove(styles.dragging);
  };

  const worldAt = (event: ReactPointerEvent, z: number): [number, number] | null => {
    const rect = hostRef.current?.getBoundingClientRect();
    const v = viewport.current;
    if (!rect || !v) return null;
    const hit = v.planPoint(event.clientX - rect.left, event.clientY - rect.top, z);
    return hit ? [hit[0], hit[1]] : null;
  };

  const start = (kind: PlanDragKind, m: PlanMarker) => (event: ReactPointerEvent<SVGElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const at = worldAt(event, m.z);
    if (!at) return;
    const target = event.currentTarget as Element;
    target.setPointerCapture(event.pointerId);
    // A drag across the panel must not select its text on the way.
    document.body.classList.add(styles.dragging);
    drag.current = {
      target,
      kind,
      id: m.id,
      // A move keeps the grab point under the pointer; a turn aims at it.
      offset: kind === 'move' ? [m.x - at[0], m.y - at[1]] : [0, 0],
      z: m.z,
      pointer: event.pointerId,
    };
  };

  const move = (event: ReactPointerEvent<SVGElement>) => {
    const d = drag.current;
    if (!d || event.pointerId !== d.pointer) return;
    const at = worldAt(event, d.z);
    if (at) onDrag?.(d.kind, d.id, [at[0] + d.offset[0], at[1] + d.offset[1]], event.shiftKey);
  };

  const end = (event: ReactPointerEvent<SVGElement>) => {
    const d = drag.current;
    if (!d || event.pointerId !== d.pointer) return;
    release();
    onDragEnd?.(d.kind, d.id);
  };

  const cancel = (event: ReactPointerEvent<SVGElement>) => {
    const d = drag.current;
    if (!d || event.pointerId !== d.pointer) return;
    release();
    onDragCancel?.();
  };

  // Esc from the host (it has put the marker back), Flyt off, or unmount: drop the gesture.
  const seenCancel = useRef(cancelToken);
  useEffect(() => {
    if (seenCancel.current === cancelToken) return;
    seenCancel.current = cancelToken;
    release();
    // `release` reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelToken]);
  useEffect(() => {
    if (editId === null) release();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => release(), []);

  return (
    <div ref={hostRef} className={cad ? `${styles.layer} ${styles.cad}` : styles.layer} aria-hidden="true">
      <svg className={styles.svg}>
        {drawn.slabPolys.map(({ g, d }) =>
          d ? (
            <path
              key={`s${g.slab.guid}`}
              d={d}
              className={`${styles.slab} ${slabPlanStyle(g.slab, cutZ) === 'above' ? styles.roof : styles.floorSlab}`}
            />
          ) : null,
        )}
        {drawn.polys.map(({ g, d }) =>
          d ? (
            <path
              key={`p${g.space.guid}`}
              d={d}
              style={fills?.has(g.space.guid) ? { fill: `var(${fills.get(g.space.guid)})` } : undefined}
              className={[
                styles.space,
                fills?.has(g.space.guid) ? styles.filled : '',
                g.ghost ? styles.ghost : '',
                g.space.kind === 'corridor' || g.space.kind === 'unknown' ? styles.corridor : '',
                g.space.guid === selectedSpace || selectedSpaces?.includes(g.space.guid) ? styles.selected : '',
              ]
                .filter(Boolean)
                .join(' ')}
            />
          ) : null,
        )}
        {drawn.marks.map(({ m, x, y, dir }) => {
          if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
          const tipX = x + dir[0] * ARROW_LEN;
          const tipY = y + dir[1] * ARROW_LEN;
          const editing = editId === m.id;
          const markCls = [styles.mark, m.selected ? styles.markSelected : '', m.pending ? styles.markPending : '']
            .filter(Boolean)
            .join(' ');
          return (
            <g key={`m${m.id}`} className={markCls}>
              {(m.selected || m.pending) && <circle className={styles.ring} cx={x} cy={y} r={MARKER_R + 5} />}
              {m.pending && <circle className={styles.pendingDot} cx={x} cy={y} r={MARKER_R - 2} />}
              {m.arrow && m.yaw !== null && (
                <>
                  <line className={styles.arrow} x1={x + dir[0] * (MARKER_R + 2)} y1={y + dir[1] * (MARKER_R + 2)} x2={tipX} y2={tipY} />
                  <path
                    className={styles.arrowHead}
                    d={`M${tipX + dir[0] * 4} ${tipY + dir[1] * 4}L${tipX - dir[0] * 4 - dir[1] * 5} ${tipY - dir[1] * 4 + dir[0] * 5}L${
                      tipX - dir[0] * 4 + dir[1] * 5
                    } ${tipY - dir[1] * 4 - dir[0] * 5}Z`}
                  />
                </>
              )}
              {editing && (
                <>
                  <circle
                    className={styles.moveHandle}
                    cx={x}
                    cy={y}
                    r={MARKER_R + 7}
                    onPointerDown={start('move', m)}
                    onPointerMove={move}
                    onPointerUp={end}
                    onPointerCancel={cancel}
                  >
                    <title>Træk for at flytte</title>
                  </circle>
                  <circle
                    className={styles.turnHandle}
                    cx={tipX + dir[0] * 10}
                    cy={tipY + dir[1] * 10}
                    r={7}
                    onPointerDown={start('turn', m)}
                    onPointerMove={move}
                    onPointerUp={end}
                    onPointerCancel={cancel}
                  >
                    <title>Træk for at dreje (Shift: 15° ad gangen)</title>
                  </circle>
                </>
              )}
            </g>
          );
        })}
      </svg>
      {drawn.polys.map(({ g, label }) => {
        if (!label || label.fit === 'none') return null;
        const sel = g.space.guid === selectedSpace || (selectedSpaces?.includes(g.space.guid) ?? false);
        return (
          <span
            key={`l${g.space.guid}`}
            className={`${styles.label}${sel ? ` ${styles.labelSelected}` : ''}${label.fit === 'two' ? ` ${styles.labelTwo}` : ''}`}
            style={{ transform: `translate(${label.x.toFixed(1)}px, ${label.y.toFixed(1)}px) translate(-50%, -50%)` }}
          >
            <span className={styles.name}>{g.text.name}</span>
            {label.fit !== 'name' && g.text.area && (
              <span className={styles.area}>
                {label.fit === 'one' ? ' · ' : ''}
                {g.text.area}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}
